/**
 * isiZulu: Billing
 *
 * What this file covers: Credits, plans, top-ups, the ledger, usage, alerts and invoices.
 *
 * Tone: Exact about money. Credits are what is spent; R is used only for rand actually paid. Never use words
 * like margin, markup or profit. Write amounts and dates the way the language writes them.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const billing: Catalog = {
  'billing.currency': 'Imali',
  'billing.fullLedger': 'Incwadi egcwele →',
  'billing.ledger.columns.when': 'Nini',
  'billing.ledger.loadError': 'Ayikwazanga ukulayisha incwadi yamabhuku.',
  'billing.ledger.title': 'Incwadi yamabhuku',
  'billing.lowBalance': 'Ibhalansi ephansi',
  'billing.emptyBalance': 'Asikho isikweletu',
  'billing.noTransactionsYet': 'Azikho izingeniso okwamanje.',
  'billing.recentTransactions': 'Izingeniso zakamuva',
  'billing.topUp': 'Gcwalisa →',
  'billing.topUpNow': 'Gcwalisa manje →',
  'billing.topup.pay': 'Khokha',
  'billing.topup.paymentMethod': 'Indlela yokukhokha',
  'billing.topup.topUp': 'Ukugcwalisa',
  'billing.walletBalance': 'Ibhalansi yesikhwama',
  'billing.alerts.low.emailPlaceholder': 'finance@example.com, owner@example.com',
  'billing.alerts.low.msisdnPlaceholder': '+27 82 000 1234',
  'billing.category.meta': 'I-Messenger ne-Instagram',
  'billing.ledger.refundedTotal': 'Kubuyiselwe',
  'billing.ledger.planFeesTotal': 'Izimali zohlelo',
  'billing.ledger.spentHint': 'Imilayezo, i-AI nesitoreji',
  'billing.ledger.planFeesHint': 'Okubhalisele kona, kususwe noma yisiphi isikweletu sokushintsha uhlelo',
  'billing.ledger.addedHint': 'Ukwengeza imali, izibonelelo namakhredithi afakwe ohlelweni lwakho',
  'billing.ledger.totalsNote': 'Okwengeziwe nokubuyiselwe, kususwe okusetshenzisiwe nezimali zohlelo, kuyibhalansi yakho uma kungekho sihlungi.',
  'billing.ledger.exportFailed': 'Ileja ayikwazanga ukukhishwa. Zama futhi.',
  'billing.ledger.exporting': 'Iyakhipha…',
  'billing.plan.limits.title': 'Okuvunyelwayo',
  'billing.plan.limit.bots': 'Ama-chatbot',
  'billing.plan.limit.numbers': 'Izinombolo ze-WhatsApp',
  'billing.plan.limit.team_members': 'Amalungu eqembu',
  'billing.plan.limit.flows': 'Ukugeleza kwe-chatbot',
  'billing.plan.limit.knowledge_documents': 'Imibhalo yesizinda solwazi',
  'billing.plan.limit.unlimited': 'Akunamkhawulo',
  'billing.wallet.bought': 'Okuthengiwe: {{amount}}',
  'billing.wallet.fromPlan': 'Okuvela ohlelweni lwakho: {{amount}}',
  'billing.wallet.split.info': 'Amakhredithi avela ohlelweni lwakho asetshenziswa kuqala futhi angawemilayezo. Imali yenyanga yohlelo lwakho ikhokhwa ngamakhredithi owathengile.',
  'billing.wallet.split.infoLabel': 'Mayelana namakhredithi athengiwe nalawo avela ohlelweni lwakho',
  'billing.plan.prices.title': 'Amanani emilayezo, ngamakhredithi',
  'billing.plan.prices.marketing': 'Umlayezo wokukhangisa',
  'billing.plan.prices.utility': 'Umlayezo wesaziso',
  'billing.plan.prices.service': 'Impendulo phakathi namahora angu-24',
  'billing.plan.prices.cheaper': 'Amanani emilayezo aphansi kunalawo owakhokha manje',
  'billing.plan.refused.title': 'Amakhredithi athengiwe awanele i-{{name}}',
  'billing.plan.refused.body': 'Imali yohlelo ikhokhwa ngamakhredithi owathengile. Amakhredithi athengiwe asele: {{bought}}. Okudingekayo: {{required}}. Okuvela ohlelweni lwakho ({{included}}) kungasetshenziswa emilayezweni kuphela.',
  'billing.plan.refused.next': 'Gcwalisa {{shortfall}} noma ngaphezulu, bese ukhetha i-{{name}} futhi.',
  'billing.usage.rates.view': 'Amanani azoboniswa',
  'billing.usage.rates.view.mine': 'Amanani akho',
  'billing.usage.rates.view.compare': 'Qhathanisa izinhlelo',
  'billing.usage.rates.changePlan': 'Shintsha uhlelo →',
  'billing.usage.rates.compareHint': 'Lokho okubizwa yiyunithi eyodwa ohlelweni ngalunye, ngamakhredithi. Uhlelo lwakho lumakiwe.',
  'billing.usage.rates.yourPlan': 'Uhlelo lwakho',
  'billing.usage.rates.yourPlanPrice': 'Inani lakho le-{{plan}}',
  'billing.usage.rates.payg': 'Inani lokukhokha njengoba usebenzisa:',
  'billing.usage.rates.planNote': 'Usohlelweni lwe-{{plan}}. Lapho inani lakho liphansi kunenani lokukhokha njengoba usebenzisa, lelo nani liboniswa lidwetshwe umugqa eduze kwelakho.',
  'billing.explain.planPrice': 'Kukhokhiswe ngenani lakho le-{{plan}} (ukukhokha njengoba usebenzisa kungu-{{list}})',
  'billing.explain.planPriceUnnamed': 'Kukhokhiswe ngenani lohlelo lwakho (ukukhokha njengoba usebenzisa kungu-{{list}})',
  'billing.plan.modalBody': 'Imali yenyanga ikhokhwa ngamakhredithi owathengile. Amakhredithi afakwe ohlelweni angezwa esikhwameni sakho njalo uma kukhokhwa, futhi asetshenziswa kuqala, emilayezweni. Uma uhlelo luphakeme, umlayezo ngamunye ubiza kancane.',
  'billing.topup.providerDisabled': 'Leyo ndlela yokukhokha isanda kuvalwa. Khetha enye, noma uzame futhi emuva kwesikhathi.',
  'billing.alerts.methods.unavailable.title': 'Ukulondoloza ikhadi akutholakali okwamanje',
  'billing.alerts.methods.unavailable.body': 'Amakhadi osuwalondolozile ahlala lapha, kodwa ukugcwalisa okuzenzakalelayo kumisiwe kuze kuvulwe futhi ukukhokha ngekhadi. Usengagcwalisa ngesandla.',
  'billing.topup.result.checking.title': 'Siqinisekisa inkokhelo yakho',
  'billing.topup.result.checking.body': 'Silinde u-{{provider}} ukuthi ayiqinisekise. Lokhu kuvame ukuthatha imizuzwana embalwa.',
  'billing.topup.result.checking.bodyPlain': 'Lokhu kuvame ukuthatha imizuzwana embalwa.',
  'billing.topup.result.slow.title': 'Sisaqinisekisa',
  'billing.topup.result.slow.body': 'U-{{provider}} akakayiqinisekisi inkokhelo yakho. Amakhredithi akho azovela lapho eseyiqinisekisile, ngakho ungakuvala lokhu.',
  'billing.topup.result.success.title': 'Inkokhelo iphumelele',
  'billing.topup.result.success.body': 'Kwengezwe {{credits}} esikhwameni sakho.',
  'billing.topup.result.success.receipt': 'Irisidi isendleleni eya ku-imeyili yakho.',
  'billing.topup.result.failed.title': 'Inkokhelo ayiphothulwanga',
  'billing.topup.result.failed.body': 'U-{{provider}} akayiphothulanga le nkokhelo, ngakho akukho makhredithi engeziwe. Uma imali iphumile ku-akhawunti yakho, xhumana nathi usebenzisa inkomba engezansi.',
  'billing.topup.result.paid': 'Kukhokhiwe',
  'billing.topup.result.added': 'Amakhredithi engeziwe',
  'billing.topup.result.balance': 'Ibhalansi manje',
  'billing.topup.result.reference': 'Inkomba {{reference}}',
  'billing.topup.result.tryAgain': 'Zama futhi',
  'billing.topup.result.done': 'Kulungile'
};

export default billing;
